import React, { useState } from 'react';
import {
  Delete,
  Description,
  Download,
  FolderOpen,
  Image,
  PictureAsPdf,
  Upload,
  VideoFile,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import {
  ContractDocument,
  useGetContractDocumentsQuery,
  useReviewContractDocumentMutation,
  useUploadContractDocumentMutation,
} from '@/apis/extended/documentsApi';
import { apiBaseUrl } from '@/apis/rawApi';
import GlassButton from '@/components/ui/GlassButton';
import GlassCard from '@/components/ui/GlassCard';

interface DocumentCenterProps {
  contractId: string;
  userRole: 'buyer' | 'seller' | 'agent';
}

const DocumentCenter: React.FC<DocumentCenterProps> = ({
  contractId,
  userRole,
}) => {
  const theme = useTheme();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentCategory, setDocumentCategory] = useState<string>('other');
  const [documentType, setDocumentType] = useState<string>('other');
  const [documentTitle, setDocumentTitle] = useState<string>('');

  const { data, isLoading } = useGetContractDocumentsQuery(contractId, {
    skip: !contractId,
  });
  const [uploadContractDocument, { isLoading: isUploading }] =
    useUploadContractDocumentMutation();
  const [reviewContractDocument, { isLoading: isReviewing }] =
    useReviewContractDocumentMutation();

  const documents = (data?.data || []) as ContractDocument[];

  const handleFileUpload = () => {
    if (selectedFile) {
      uploadContractDocument({
        contractId,
        file: selectedFile,
        type: documentType,
        category: documentCategory,
        title: documentTitle,
      })
        .unwrap()
        .then(() => {
          setUploadDialogOpen(false);
          setSelectedFile(null);
          setDocumentTitle('');
          setDocumentType('other');
          setDocumentCategory('other');
        })
        .catch((err) => {
          console.error('Upload failed', err);
          alert('Upload failed. Please try again.');
        });
    }
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <PictureAsPdf color="error" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <Image color="primary" />;
      case 'mp4':
      case 'avi':
        return <VideoFile color="secondary" />;
      default:
        return <Description color="action" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending_review':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'contract':
        return 'primary';
      case 'inspection':
        return 'secondary';
      case 'mortgage':
        return 'success';
      case 'title':
        return 'info';
      case 'disclosures':
        return 'warning';
      case 'insurance':
        return 'error';
      default:
        return 'default';
    }
  };

  const groupedDocuments = documents.reduce((acc, doc) => {
    const cat = doc.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {} as Record<string, ContractDocument[]>);

  const fileExt = (doc: ContractDocument) => {
    const name = doc.file?.originalName || doc.file?.filename || '';
    const ext = name.split('.').pop() || '';
    return ext.toLowerCase();
  };

  const handleDownload = (doc: ContractDocument) => {
    window.open(`${apiBaseUrl}/documents/${doc._id}/download`, '_blank');
  };

  const canApproveReject = (doc: ContractDocument) => {
    // MVP UI gating; backend is source of truth.
    if (userRole === 'buyer')
      return [
        'inspection_report',
        'loan_approval',
        'homeowners_insurance',
      ].includes(doc.type);
    if (userRole === 'seller') return ['seller_disclosures'].includes(doc.type);
    if (userRole === 'agent')
      return ['title_commitment', 'purchase_agreement', 'other'].includes(
        doc.type
      );
    return false;
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography
          variant="h5"
          component="h2"
          sx={{ color: theme.palette.mainColors.primary }}
        >
          Document Center
        </Typography>
        <GlassButton
          variant="contained"
          startIcon={<Upload />}
          onClick={() => setUploadDialogOpen(true)}
        >
          Upload Document
        </GlassButton>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        All parties can view and upload documents. Documents are automatically
        shared with all stakeholders.
      </Alert>

      <Grid container spacing={3}>
        {Object.entries(groupedDocuments).map(([category, docs]) => (
          <Grid item xs={12} md={6} key={category}>
            <GlassCard variant="light">
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <FolderOpen sx={{ mr: 1 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      textTransform: 'capitalize',
                      color: theme.palette.mainColors.white,
                    }}
                  >
                    {category} Documents
                  </Typography>
                  <Chip
                    label={docs.length}
                    size="small"
                    color={getCategoryColor(category)}
                    sx={{ ml: 'auto' }}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {docs.map((doc) => (
                    <ListItem key={doc._id} divider>
                      <Box display="flex" alignItems="center" sx={{ mr: 2 }}>
                        {getFileIcon(fileExt(doc))}
                      </Box>
                      <ListItemText
                        primary={
                          doc.title || doc.file?.originalName || 'Document'
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              {doc.file?.size
                                ? `${Math.round(doc.file.size / 1024)} KB`
                                : ''}{' '}
                              • Uploaded by {doc.uploadedByRole} on{' '}
                              {new Date(doc.createdAt).toLocaleDateString()}
                            </Typography>
                            <Chip
                              label={doc.status}
                              size="small"
                              color={getStatusColor(doc.status) as any}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="download"
                          onClick={() => handleDownload(doc)}
                        >
                          <Download />
                        </IconButton>

                        {canApproveReject(doc) && (
                          <>
                            <Button
                              size="small"
                              sx={{ ml: 1 }}
                              variant="outlined"
                              disabled={isReviewing}
                              onClick={() =>
                                reviewContractDocument({
                                  documentId: doc._id,
                                  decision: 'approved',
                                })
                                  .unwrap()
                                  .catch((e) => {
                                    console.error(e);
                                    alert('Approve failed.');
                                  })
                              }
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              sx={{ ml: 1 }}
                              variant="outlined"
                              color="error"
                              disabled={isReviewing}
                              onClick={() => {
                                const reason =
                                  prompt('Rejection reason (optional):') || '';
                                reviewContractDocument({
                                  documentId: doc._id,
                                  decision: 'rejected',
                                  comment: reason,
                                })
                                  .unwrap()
                                  .catch((e) => {
                                    console.error(e);
                                    alert('Reject failed.');
                                  });
                              }}
                            >
                              Reject
                            </Button>
                          </>
                        )}

                        {userRole === 'agent' && (
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            color="error"
                            disabled
                            title="Delete will be enabled when backend delete endpoint is added"
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </GlassCard>
          </Grid>
        ))}
      </Grid>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Document Type"
            select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            SelectProps={{ native: true }}
          >
            <option value="purchase_agreement">Purchase Agreement</option>
            <option value="inspection_report">Inspection Report</option>
            <option value="loan_approval">Loan Approval Letter</option>
            <option value="seller_disclosures">Seller Disclosures</option>
            <option value="title_commitment">Title Commitment</option>
            <option value="homeowners_insurance">Homeowners Insurance</option>
            <option value="other">Other</option>
          </TextField>

          <TextField
            fullWidth
            margin="normal"
            label="Title (optional)"
            value={documentTitle}
            onChange={(e) => setDocumentTitle(e.target.value)}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Category"
            select
            value={documentCategory}
            onChange={(e) => setDocumentCategory(e.target.value)}
            SelectProps={{ native: true }}
          >
            <option value="disclosures">Disclosures</option>
            <option value="contract">Contract</option>
            <option value="title">Title</option>
            <option value="insurance">Insurance</option>
            <option value="mortgage">Mortgage</option>
            <option value="inspection">Inspection</option>
            <option value="other">Other</option>
          </TextField>
          <Box mt={2}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<Upload />}
              fullWidth
            >
              {selectedFile ? selectedFile.name : 'Choose File'}
              <input
                type="file"
                hidden
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={() => setUploadDialogOpen(false)}>
            Cancel
          </GlassButton>
          <GlassButton
            onClick={handleFileUpload}
            variant="contained"
            disabled={!selectedFile || isUploading}
          >
            Upload
          </GlassButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentCenter;
